import { useRoute, useRouter } from 'vue-router';
import {
  RETURN_QUERY_KEY,
  queryWithoutReturnPath,
  safeReturnPath,
} from '@/js/core/utils/return-path.js';
import { spaModalOrigin } from '@/js/core/utils/modal-origin';

const DEFAULT_CLOSE_ROUTE = { name: 'home' };

export function useModalClose({ fallbackRoute = DEFAULT_CLOSE_ROUTE, cleanup = null } = {}) {
  const route = useRoute();
  const router = useRouter();

  const runCleanup = async () => {
    if (typeof cleanup === 'function') {
      await cleanup();
    }
  };

  const closeModal = async () => {
    // Modals opened from a block on a content page carry that page in `return`.
    const returnPath = safeReturnPath(route.query[RETURN_QUERY_KEY]);
    if (returnPath) {
      const origin = spaModalOrigin();

      // An in-app push left the origin page in memory, so the router can restore it
      // with no document round-trip. Anything else was a native page load, and only a
      // document navigation can rebuild it — the SPA never re-hydrates Gutenberg
      // content client-side.
      //
      // `return` survives a pushed navigation because the server stamps it on the link
      // itself. Matching it against the recorded origin keeps a stale origin, left by
      // some earlier navigation, from standing in for the page this modal came from.
      if (origin && origin.path === router.resolve(returnPath).path) {
        await runCleanup();

        return router.replace(origin);
      }

      // Cleanup is skipped on purpose here: it only resets in-memory state, which the
      // document load discards anyway, and unmounting the modal first would flash the
      // index page underneath while the next document is still loading.
      // replace(), not assign(), so Back does not reopen the modal just closed.
      window.location.replace(returnPath);
      return;
    }

    await runCleanup();

    return router.replace(
      resolveCloseRoute(route.meta.modalCloseTo || fallbackRoute, routeWithoutReturn(route))
    );
  };

  return {
    closeModal,
  };
}

function resolveCloseRoute(closeRoute, route) {
  if (typeof closeRoute === 'function') {
    return closeRoute(route);
  }

  return closeRoute || DEFAULT_CLOSE_ROUTE;
}

// A rejected return value still sits in the query, and close routes may forward the whole
// query, so drop it before the fallback can carry it through.
function routeWithoutReturn(route) {
  const query = queryWithoutReturnPath(route.query);

  return query === route.query ? route : { ...route, query };
}
